import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import BrandDashboardLayout from "../../components/BrandDashboardLayout";
import { toast } from "sonner";
import { Users, UserPlus, X, Shield, ShieldCheck, MailCheck, Globe, Trash2, Plus, ArrowRight, Building2, LogOut, Crown } from "lucide-react";

const ROLE_LABEL = { owner: "Workspace Owner", admin: "Organisation Admin", member: "Member" };

export default function Organisation() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [editing, setEditing] = useState(false);
  const [domainInput, setDomainInput] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/orgs/me").then(({ data }) => setData(data))
      .catch(e => toast.error(e.response?.data?.detail || "Failed to load org"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (loading) return <BrandDashboardLayout><div className="p-12 text-ink-muted">Loading…</div></BrandDashboardLayout>;
  if (!data?.organisation) return (
    <BrandDashboardLayout>
      <div className="p-12 max-w-2xl">
        <div className="label-eyebrow">Organisation</div>
        <h1 className="font-heading text-4xl uppercase mt-2">No organisation</h1>
        <p className="text-ink-muted mt-3 text-sm">Your account isn't attached to an organisation yet. Contact support.</p>
      </div>
    </BrandDashboardLayout>
  );

  const org = data.organisation;
  const isOwner = data.my_role === "owner";
  const isAdmin = data.my_role === "admin" || isOwner;

  const addDomain = async () => {
    if (!domainInput.trim()) return;
    try {
      await api.post("/orgs/me/trusted-domains", { domain: domainInput.trim().toLowerCase() });
      setDomainInput("");
      toast.success("Trusted domain added");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const removeDomain = async (d) => {
    try { await api.delete(`/orgs/me/trusted-domains/${encodeURIComponent(d)}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const removeMember = async (uid) => {
    if (!window.confirm("Remove this member from the organisation? Their work stays with the org.")) return;
    // Optimistic — drop from the list immediately
    const snapshot = data;
    setData(d => ({ ...d, members: d.members.filter(m => m.id !== uid) }));
    try { await api.delete(`/orgs/me/members/${uid}`); toast.success("Member removed"); load(); }
    catch (e) { setData(snapshot); toast.error(e.response?.data?.detail || "Failed"); }
  };
  const changeRole = async (uid, role) => {
    const snapshot = data;
    setData(d => ({ ...d, members: d.members.map(m => m.id === uid ? { ...m, org_role: role } : m) }));
    try { await api.patch(`/orgs/me/members/${uid}/role`, { role }); }
    catch (e) { setData(snapshot); toast.error(e.response?.data?.detail || "Failed"); }
  };
  const revokeInvite = async (iid) => {
    const snapshot = data;
    setData(d => ({ ...d, invites: d.invites.filter(i => i.id !== iid) }));
    try { await api.delete(`/orgs/me/invites/${iid}`); }
    catch (e) { setData(snapshot); toast.error(e.response?.data?.detail || "Failed"); }
  };
  const transferOwnership = async (uid, displayName) => {
    if (!window.confirm(`Transfer Workspace Ownership to ${displayName}? You'll become an Admin and they'll take over billing + workspace settings.`)) return;
    try {
      await api.post(`/orgs/me/transfer-ownership/${uid}`);
      toast.success("Workspace ownership transferred");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const leaveOrg = async () => {
    const msg = data.my_role === "owner"
      ? "You're a Workspace Owner. Transfer ownership first."
      : "Leave this organisation? Your campaigns and conversations stay with the org. You can be re-invited later.";
    if (data.my_role === "owner") { toast.error(msg); return; }
    if (!window.confirm(msg)) return;
    try {
      await api.post("/orgs/me/leave");
      toast.success("You've left the organisation");
      window.location.href = "/onboarding";
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <BrandDashboardLayout>
      <div className="p-6 md:p-12 max-w-5xl">
        <div className="mb-10">
          <div className="label-eyebrow">Workspace</div>
          <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2" data-testid="org-title">Organisation</h1>
          <p className="text-ink-muted mt-2 text-sm max-w-xl">Your workspace, your team, your campaigns. Athletes you work with stay tied to the organisation — not a single user.</p>
        </div>

        {/* Org identity */}
        <div className="surface p-6 mb-8" data-testid="org-identity">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-4 min-w-0">
              <div className="w-12 h-12 bg-bg-elev border border-line flex items-center justify-center shrink-0">
                {org.organisation_logo ? <img src={org.organisation_logo} alt="" className="w-full h-full object-cover" /> : <Building2 className="w-6 h-6 text-volt" />}
              </div>
              <div className="min-w-0">
                <div className="font-heading text-2xl uppercase tracking-tight" data-testid="org-name">{org.organisation_name}</div>
                <div className="flex gap-2 mt-1 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest text-ink-muted">{org.type === "brand" ? "Brand workspace" : "Agency workspace"}</span>
                  <span className="text-[10px] uppercase tracking-widest text-volt">{org.plan} plan</span>
                  {org.verification_status === "domain_verified" && (
                    <span className="text-[10px] uppercase tracking-widest text-volt inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Verified</span>
                  )}
                </div>
                {org.website && <a href={org.website} target="_blank" rel="noreferrer" className="text-xs text-ink-muted mt-2 inline-block">{org.website}</a>}
              </div>
            </div>
            {isAdmin && <button onClick={() => setEditing(true)} className="btn-ghost-line text-xs" data-testid="org-edit-btn">Edit details</button>}
          </div>
        </div>

        {/* Members */}
        <div className="surface mb-8" data-testid="org-members">
          <div className="p-6 border-b border-line flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="label-eyebrow">Team</div>
              <div className="font-heading text-2xl uppercase mt-1">Members <span className="text-volt font-mono text-lg ml-2">{data.members.length}</span></div>
              <p className="text-xs text-ink-muted mt-1">Everyone here can collaborate on campaigns, outreach and shortlists.</p>
            </div>
            {isAdmin && <button onClick={() => setShowInvite(true)} className="btn-volt text-sm inline-flex items-center gap-2" data-testid="invite-member-btn">
              <UserPlus className="w-4 h-4" /> Invite teammate
            </button>}
          </div>
          <div className="divide-y divide-line">
            {data.members.map(m => (
              <div key={m.id} className="px-6 py-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`member-row-${m.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{m.full_name} {m.id === user?.id && <span className="text-[10px] uppercase tracking-widest text-volt ml-1">You</span>}</div>
                  <div className="text-xs text-ink-muted truncate">{m.email}</div>
                </div>
                <div className="flex items-center gap-3">
                  {m.email_verified && <span className="text-[10px] uppercase tracking-widest text-volt inline-flex items-center gap-1"><MailCheck className="w-3 h-3" /> Verified</span>}
                  {isOwner && m.id !== user?.id ? (
                    <select value={m.org_role || "member"} onChange={e => changeRole(m.id, e.target.value)} data-testid={`member-role-${m.id}`}
                      className="bg-bg-elev border border-line text-[10px] uppercase tracking-widest px-2 py-1 text-white">
                      <option value="owner">Owner</option>
                      <option value="admin">Admin</option>
                      <option value="member">Member</option>
                    </select>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest text-ink-muted">{ROLE_LABEL[m.org_role] || "Member"}</span>
                  )}
                  {isOwner && m.id !== user?.id && m.org_role !== "owner" && (
                    <button onClick={() => transferOwnership(m.id, m.full_name || m.email)} title="Transfer ownership" data-testid={`transfer-ownership-${m.id}`}
                      className="text-ink-muted hover:text-volt">
                      <Crown className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {isAdmin && m.id !== user?.id && m.org_role !== "owner" && (
                    <button onClick={() => removeMember(m.id)} className="text-ink-muted hover:text-[#FF3B30]" data-testid={`remove-member-${m.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pending invites */}
        {data.invites?.length > 0 && (
          <div className="surface mb-8" data-testid="pending-invites">
            <div className="p-6 border-b border-line">
              <div className="label-eyebrow">Pending</div>
              <div className="font-heading text-xl uppercase mt-1">Invitations</div>
            </div>
            <div className="divide-y divide-line">
              {data.invites.map(i => (
                <div key={i.id} className="px-6 py-3 flex items-center justify-between gap-3" data-testid={`invite-row-${i.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm truncate">{i.email}</div>
                    <div className="text-[10px] uppercase tracking-widest text-ink-muted mt-1">{i.role} · expires {i.expires_at?.slice(0, 10)}</div>
                  </div>
                  {isAdmin && <button onClick={() => revokeInvite(i.id)} className="text-xs text-ink-muted hover:text-[#FF3B30] uppercase tracking-widest" data-testid={`revoke-invite-${i.id}`}>Revoke</button>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Trusted domains */}
        {isAdmin && (
          <div className="surface mb-8" data-testid="trusted-domains">
            <div className="p-6 border-b border-line">
              <div className="label-eyebrow flex items-center gap-2"><Globe className="w-3 h-3" /> Trust</div>
              <div className="font-heading text-xl uppercase mt-1">Trusted domains</div>
              <p className="text-xs text-ink-muted mt-2">When someone signs up with one of these domains, we'll suggest joining your organisation. Used for verification only — never for permissions.</p>
            </div>
            <div className="p-6">
              <div className="flex flex-wrap gap-2 mb-4">
                {(org.trusted_domains || []).map(d => (
                  <span key={d} data-testid={`domain-${d}`} className="inline-flex items-center gap-2 border border-line px-3 py-1.5 text-xs">
                    {d}
                    <button onClick={() => removeDomain(d)} className="text-ink-muted hover:text-[#FF3B30]" data-testid={`remove-domain-${d}`}><X className="w-3 h-3" /></button>
                  </span>
                ))}
                {(org.trusted_domains || []).length === 0 && <span className="text-xs text-ink-muted">No trusted domains yet.</span>}
              </div>
              <div className="flex gap-2">
                <input value={domainInput} onChange={e => setDomainInput(e.target.value)} placeholder="e.g. nike.com" data-testid="add-domain-input"
                  className="flex-1 bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm" />
                <button onClick={addDomain} disabled={!domainInput.trim()} className="btn-volt text-sm disabled:opacity-40" data-testid="add-domain-btn">Add</button>
              </div>
            </div>
          </div>
        )}

        {/* Danger zone */}
        <div className="surface border-l-2 border-[#FF3B30] p-6 mb-8" data-testid="org-danger-zone">
          <div className="label-eyebrow text-[#FF3B30]">Danger zone</div>
          <div className="font-heading text-xl uppercase mt-1">Leave organisation</div>
          <p className="text-xs text-ink-muted mt-2">
            {isOwner ? "You're a Workspace Owner. Transfer ownership to another member before you can leave." :
              "You'll lose access to this organisation's campaigns, athletes, and inbox. Your work stays with the org."}
          </p>
          <button onClick={leaveOrg} disabled={isOwner} data-testid="leave-org-btn"
            className="mt-4 px-4 py-2 border border-[#FF3B30] text-[#FF3B30] text-xs uppercase tracking-widest hover:bg-[#FF3B30] hover:text-white disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#FF3B30] inline-flex items-center gap-2">
            <LogOut className="w-3.5 h-3.5" /> Leave organisation
          </button>
        </div>

        {showInvite && <InviteModal onClose={() => setShowInvite(false)} onCreated={() => { setShowInvite(false); load(); }} />}
        {editing && <EditOrgModal org={org} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}
      </div>
    </BrandDashboardLayout>
  );
}

function InviteModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", role: "member" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.email.trim()) return toast.error("Email is required");
    setBusy(true);
    try {
      await api.post("/orgs/me/invites", { email: form.email.trim().toLowerCase(), role: form.role });
      toast.success(`Invitation sent to ${form.email}`);
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="surface w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="invite-modal">
        <div className="p-6 border-b border-line flex items-start justify-between">
          <div>
            <div className="label-eyebrow">Invite</div>
            <h2 className="font-heading text-2xl uppercase tracking-tighter mt-2">Add a teammate</h2>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Work email">
            <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} type="email" data-testid="invite-email"
              placeholder="teammate@yourcompany.com" className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm" />
          </Field>
          <Field label="Role">
            <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} data-testid="invite-role"
              className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm">
              <option value="member">Member — basic collaboration</option>
              <option value="admin">Admin — manage members + verification</option>
              <option value="owner">Owner — full workspace control + billing</option>
            </select>
          </Field>
          <p className="text-[10px] text-ink-muted">We'll email them a magic link. The invitation expires in 14 days.</p>
        </div>
        <div className="p-6 border-t border-line flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost-line text-sm">Cancel</button>
          <button onClick={submit} disabled={busy || !form.email} className="btn-volt text-sm disabled:opacity-40" data-testid="invite-submit">
            {busy ? "Sending…" : "Send invitation"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditOrgModal({ org, onClose, onSaved }) {
  const [form, setForm] = useState({
    organisation_name: org.organisation_name || "",
    website: org.website || "",
    industry: org.industry || "",
    headquarters_region: org.headquarters_region || "",
    organisation_logo: org.organisation_logo || "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.patch("/orgs/me", form); toast.success("Saved"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="surface w-full max-w-lg" onClick={e => e.stopPropagation()} data-testid="edit-org-modal">
        <div className="p-6 border-b border-line flex items-start justify-between">
          <div>
            <div className="label-eyebrow">Organisation</div>
            <h2 className="font-heading text-2xl uppercase tracking-tighter mt-2">Edit details</h2>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Name">
            <input value={form.organisation_name} onChange={e => setForm(f => ({ ...f, organisation_name: e.target.value }))} data-testid="org-name-input"
              className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Website">
              <input value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
                placeholder="https://acme.com" className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm" />
            </Field>
            <Field label="Industry">
              <input value={form.industry} onChange={e => setForm(f => ({ ...f, industry: e.target.value }))}
                placeholder="Performance nutrition" className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm" />
            </Field>
          </div>
          <Field label="HQ region">
            <input value={form.headquarters_region} onChange={e => setForm(f => ({ ...f, headquarters_region: e.target.value }))}
              placeholder="London, UK" className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm" />
          </Field>
          <Field label="Logo URL">
            <input value={form.organisation_logo} onChange={e => setForm(f => ({ ...f, organisation_logo: e.target.value }))}
              placeholder="https://…/logo.png" className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm" />
          </Field>
        </div>
        <div className="p-6 border-t border-line flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost-line text-sm">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-volt text-sm disabled:opacity-40" data-testid="org-save-btn">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="label-eyebrow block mb-2 text-[10px]">{label}</label>{children}</div>;
}
